import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { showToast } from "../helper/notifications";
import { Icon } from "@iconify/react";

/**
 * Export to Google Business Profile Modal
 * Executes DB-First snapshot update followed by live GBP API synchronization
 * with real-time progress bar, coordinates, opening hours, and photos upload.
 */
const ExportToGbpModal = ({
  isOpen = false,
  onClose,
  preSelectedIds = [],
  onExportCompleted,
}) => {
  const navigate = useNavigate();

  // Modal Workflow State: 'configure' | 'exporting' | 'completed'
  const [step, setStep] = useState("configure");

  // Accounts & Candidates State
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [candidates, setCandidates] = useState([]);

  // Selection Filter State
  const [filterMode, setFilterMode] = useState("all"); // 'all' | 'new' | 'updates' | 'selected'
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Execution & Progress State
  const [isExporting, setIsExporting] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeItem, setActiveItem] = useState(null);
  const [activeItemStep, setActiveItemStep] = useState(""); // 'db' | 'api' | 'photos' | 'done' | 'error'
  const [progressPercent, setProgressPercent] = useState(0);
  const [logs, setLogs] = useState([]);
  const [exportStats, setExportStats] = useState({
    total: 0,
    created: 0,
    updated: 0,
    photosUploaded: 0,
    failed: 0,
  });

  const abortControllerRef = useRef(false);
  const logContainerRef = useRef(null);

  // Auto-scroll logs
  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs]);

  // Load accounts and export preview data
  const loadInitialData = useCallback(async () => {
    setLoadingInitial(true);
    try {
      // 1. Fetch connected Google Business accounts
      const accountsRes = await api.get("/google-business/accounts");
      const connected = Boolean(accountsRes.data?.isConnected);
      const accList = accountsRes.data?.accounts || [];
      setIsConnected(connected);
      setAccounts(accList);
      if (accList.length > 0) {
        setSelectedAccountId(accList[0].accountId);
      }

      // 2. Fetch candidate locations
      const previewRes = await api.get("/main-locations/export-preview");
      const locList = previewRes.data?.data || [];
      setCandidates(locList);

      // Pre-select only approved locations
      const approvedIds = new Set(
        locList.filter((l) => Boolean(l.publish_google_business)).map((l) => l.location_id)
      );

      if (preSelectedIds && preSelectedIds.length > 0) {
        const validPreSelected = preSelectedIds.filter((id) => approvedIds.has(id));
        setSelectedIds(new Set(validPreSelected));
        setFilterMode("selected");
      } else {
        setSelectedIds(approvedIds);
      }
    } catch (err) {
      console.error("Failed to load export preview data:", err);
      showToast("error", "Failed to load Google Business export data.");
    } finally {
      setLoadingInitial(false);
    }
  }, [preSelectedIds]);

  useEffect(() => {
    if (isOpen) {
      setStep("configure");
      setIsExporting(false);
      setProgressPercent(0);
      setLogs([]);
      setExportStats({ total: 0, created: 0, updated: 0, photosUploaded: 0, failed: 0 });
      loadInitialData();
    }
  }, [isOpen, loadInitialData]);

  // Filtered candidate list based on active tab
  const displayedCandidates = candidates.filter((loc) => {
    if (filterMode === "approved") return Boolean(loc.publish_google_business);
    if (filterMode === "new") return loc.gbp_status === "NEW";
    if (filterMode === "updates") return loc.gbp_status === "EXISTS";
    if (filterMode === "selected") return selectedIds.has(loc.location_id);
    return true;
  });

  // Toggle selection for a single location
  const toggleLocationSelection = (locId) => {
    const candidate = candidates.find((c) => c.location_id === locId);
    if (candidate && !candidate.publish_google_business) {
      showToast("warning", `"${candidate.location_name}" is not approved for Google Business Profile publishing.`);
      return;
    }
    const next = new Set(selectedIds);
    if (next.has(locId)) {
      next.delete(locId);
    } else {
      next.add(locId);
    }
    setSelectedIds(next);
  };

  // Toggle select all in displayed view (only approved locations)
  const toggleSelectAllDisplayed = () => {
    const selectableDisplayedIds = displayedCandidates
      .filter((c) => Boolean(c.publish_google_business))
      .map((c) => c.location_id);
    if (selectableDisplayedIds.length === 0) return;

    const allSelected = selectableDisplayedIds.every((id) => selectedIds.has(id));
    const next = new Set(selectedIds);

    if (allSelected) {
      selectableDisplayedIds.forEach((id) => next.delete(id));
    } else {
      selectableDisplayedIds.forEach((id) => next.add(id));
    }
    setSelectedIds(next);
  };

  // Append entry to live log
  const appendLog = (type, message, details = null) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs((prev) => [...prev, { id: Date.now() + Math.random(), timestamp, type, message, details }]);
  };

  // Start Batch Export Process
  const handleStartExport = async () => {
    const locationsToExport = candidates.filter((loc) => selectedIds.has(loc.location_id));

    if (locationsToExport.length === 0) {
      showToast("warning", "Please select at least one location to export.");
      return;
    }

    if (!selectedAccountId) {
      showToast("warning", "Please select a target Google Business Account.");
      return;
    }

    setStep("exporting");
    setIsExporting(true);
    abortControllerRef.current = false;

    const totalCount = locationsToExport.length;
    let createdCount = 0;
    let updatedCount = 0;
    let photosCount = 0;
    let failedCount = 0;

    appendLog("info", `🚀 Starting export of ${totalCount} location(s) to Google Business Profile...`);
    appendLog("info", `Target Account: ${selectedAccountId}`);

    for (let i = 0; i < totalCount; i++) {
      if (abortControllerRef.current) {
        appendLog("warning", "🛑 Export process cancelled by user.");
        break;
      }

      const loc = locationsToExport[i];
      setCurrentIndex(i + 1);
      setActiveItem(loc);
      setProgressPercent(Math.round(((i) / totalCount) * 100));

      appendLog("process", `[${i + 1}/${totalCount}] Processing "${loc.location_name}" (Store Code: ${loc.store_code})...`);

      // Step 1: Database-First Sync Notification
      setActiveItemStep("db");
      appendLog("info", `  💾 Step 1/3: Storing version snapshot in MySQL database...`);

      try {
        // Step 2 & 3: Call single export endpoint
        setActiveItemStep("api");
        appendLog("info", `  🌐 Step 2/3: Calling Google Business API (${loc.gbp_status === "NEW" ? "Creating New" : "Updating Profile"})...`);

        const res = await api.post(`/main-locations/${loc.location_id}/export-to-google`, {
          targetAccountId: selectedAccountId,
        });

        const resData = res.data?.data || {};

        if (resData.photos_uploaded > 0) {
          setActiveItemStep("photos");
          appendLog("info", `  📸 Step 3/3: Synced ${resData.photos_uploaded} photo(s) to Google Business Media...`);
          photosCount += resData.photos_uploaded;
        }

        if (resData.action === "CREATED") {
          createdCount++;
          appendLog("success", `  ✅ Created new Google Profile: ${resData.google_location_id} (Version ${resData.version_number})`);
        } else {
          updatedCount++;
          appendLog("success", `  ✅ Updated Google Profile: ${resData.google_location_id} (Version ${resData.version_number})`);
        }

        setActiveItemStep("done");
      } catch (err) {
        failedCount++;
        setActiveItemStep("error");
        const errMsg = err.response?.data?.error || err.response?.data?.message || err.message || "Unknown error";
        appendLog("error", `  ❌ Failed to export "${loc.location_name}": ${errMsg}`);
      }

      setExportStats({
        total: totalCount,
        created: createdCount,
        updated: updatedCount,
        photosUploaded: photosCount,
        failed: failedCount,
      });

      setProgressPercent(Math.round(((i + 1) / totalCount) * 100));
    }

    setIsExporting(false);
    setActiveItem(null);
    setActiveItemStep("");
    setStep("completed");
    appendLog("success", `🎉 Batch export completed: ${createdCount} created, ${updatedCount} updated, ${failedCount} failed.`);

    if (onExportCompleted) {
      onExportCompleted();
    }
  };

  // Stop / Abort export
  const handleStopExport = () => {
    abortControllerRef.current = true;
    setIsExporting(false);
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      style={{ backgroundColor: "rgba(15, 23, 42, 0.65)", backdropFilter: "blur(4px)", zIndex: 1055 }}
    >
      <div className="modal-dialog modal-dialog-centered modal-xl" style={{ maxWidth: "950px" }}>
        <div className="modal-content border-0 shadow-lg radius-16 overflow-hidden bg-base">
          {/* MODAL HEADER */}
          <div className="modal-header border-bottom border-neutral-200 px-24 py-16 d-flex align-items-center justify-content-between bg-neutral-50">
            <div className="d-flex align-items-center gap-3">
              <div className="w-40-px h-40-px radius-10 bg-primary-100 text-primary-600 d-flex align-items-center justify-content-center">
                <Icon icon="logos:google-icon" className="text-xl" />
              </div>
              <div>
                <h6 className="modal-title mb-0 fw-bold text-primary-light">
                  Export Changes to Google Business Profiles
                </h6>
                <p className="text-xs text-secondary-light mb-0">
                  Database-First synchronization of master locations, coordinates, opening hours, and photos.
                </p>
              </div>
            </div>
            {!isExporting && (
              <button
                type="button"
                className="btn-close text-neutral-500"
                onClick={onClose}
                aria-label="Close"
              />
            )}
          </div>

          {/* MODAL BODY */}
          <div className="modal-body p-24">
            {loadingInitial ? (
              <div className="text-center py-48">
                <Icon icon="line-md:loading-twotone-loop" className="text-4xl text-primary-600 mb-12" />
                <p className="text-sm text-secondary-light">Loading candidate locations and Google connection status...</p>
              </div>
            ) : !isConnected ? (
              /* GOOGLE NOT CONNECTED STATE */
              <div className="text-center py-36 px-24 bg-neutral-50 radius-12 border border-neutral-200">
                <div className="w-64-px h-64-px radius-circle bg-warning-100 text-warning-600 d-inline-flex align-items-center justify-content-center mb-16">
                  <Icon icon="solar:shield-warning-bold" className="text-3xl" />
                </div>
                <h6 className="fw-bold text-primary-light mb-8">Google Business Account Not Connected</h6>
                <p className="text-sm text-secondary-light mb-20" style={{ maxWidth: "520px", margin: "0 auto" }}>
                  To export locations directly to Google Business Profile APIs, please authenticate your Google account in the Google Business section.
                </p>
                <button
                  type="button"
                  className="btn btn-primary-600 px-24 py-10 radius-8 fw-semibold d-inline-flex align-items-center gap-2"
                  onClick={() => {
                    onClose();
                    navigate("/google-business");
                  }}
                >
                  <Icon icon="logos:google-icon" /> Connect Google Business Account
                </button>
              </div>
            ) : step === "configure" ? (
              /* STEP 1: CONFIGURE & PREVIEW */
              <>
                {/* Account Selector Card */}
                <div className="card bg-neutral-50 border border-neutral-200 radius-12 p-16 mb-20">
                  <div className="row g-3 align-items-center">
                    <div className="col-md-7">
                      <label className="form-label text-xs fw-bold text-secondary-light mb-4">
                        TARGET GOOGLE BUSINESS ACCOUNT
                      </label>
                      <select
                        className="form-select form-select-sm radius-8 fw-semibold text-primary-light bg-white"
                        value={selectedAccountId}
                        onChange={(e) => setSelectedAccountId(e.target.value)}
                      >
                        {accounts.map((acc) => (
                          <option key={acc.accountId} value={acc.accountId}>
                            {acc.accountName} ({acc.accountId}) - {acc.type}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-5">
                      <div className="d-flex align-items-center justify-content-md-end gap-2 mt-md-4">
                        <span className="badge bg-success-50 text-success-700 border border-success-200 px-10 py-6 radius-6 text-xs d-flex align-items-center gap-1">
                          <Icon icon="solar:check-circle-bold" /> Google Account Connected
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Filter Tabs & Selection Summary */}
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-12">
                  <div className="btn-group radius-8 overflow-hidden border">
                    <button
                      className={`btn btn-xs px-12 py-6 ${filterMode === "all" ? "btn-primary" : "btn-light text-neutral-600"}`}
                      onClick={() => setFilterMode("all")}
                    >
                      All ({candidates.length})
                    </button>
                    <button
                      className={`btn btn-xs px-12 py-6 ${filterMode === "approved" ? "btn-primary" : "btn-light text-neutral-600"}`}
                      onClick={() => setFilterMode("approved")}
                    >
                      Approved ({candidates.filter((c) => Boolean(c.publish_google_business)).length})
                    </button>
                    <button
                      className={`btn btn-xs px-12 py-6 ${filterMode === "new" ? "btn-primary" : "btn-light text-neutral-600"}`}
                      onClick={() => setFilterMode("new")}
                    >
                      New to GBP ({candidates.filter((c) => c.gbp_status === "NEW").length})
                    </button>
                    <button
                      className={`btn btn-xs px-12 py-6 ${filterMode === "updates" ? "btn-primary" : "btn-light text-neutral-600"}`}
                      onClick={() => setFilterMode("updates")}
                    >
                      Updates ({candidates.filter((c) => c.gbp_status === "EXISTS").length})
                    </button>
                    <button
                      className={`btn btn-xs px-12 py-6 ${filterMode === "selected" ? "btn-primary" : "btn-light text-neutral-600"}`}
                      onClick={() => setFilterMode("selected")}
                    >
                      Selected ({selectedIds.size})
                    </button>
                  </div>

                  <div className="d-flex align-items-center gap-2">
                    <button
                      type="button"
                      className="btn btn-xs btn-outline-secondary px-12 py-6 radius-6 text-xs fw-semibold"
                      onClick={toggleSelectAllDisplayed}
                    >
                      Toggle All Approved in View
                    </button>
                    <span className="text-xs fw-semibold text-secondary-light">
                      {selectedIds.size} of {candidates.filter((c) => Boolean(c.publish_google_business)).length} approved selected
                    </span>
                  </div>
                </div>

                {/* Candidates List Table */}
                <div
                  className="table-responsive border border-neutral-200 radius-10 mb-16"
                  style={{ maxHeight: "300px", overflowY: "auto" }}
                >
                  <table className="table table-hover align-middle mb-0 text-xs">
                    <thead className="table-light sticky-top" style={{ zIndex: 1 }}>
                      <tr>
                        <th style={{ width: "40px" }} className="text-center">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={
                              displayedCandidates.some((c) => Boolean(c.publish_google_business)) &&
                              displayedCandidates
                                .filter((c) => Boolean(c.publish_google_business))
                                .every((c) => selectedIds.has(c.location_id))
                            }
                            onChange={toggleSelectAllDisplayed}
                          />
                        </th>
                        <th>Location Name & Store Code</th>
                        <th>Address & City</th>
                        <th>Publishing</th>
                        <th>Coordinates</th>
                        <th>Hours</th>
                        <th>Photos</th>
                        <th>GBP Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayedCandidates.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="text-center py-24 text-secondary-light">
                            No locations match the selected filter.
                          </td>
                        </tr>
                      ) : (
                        displayedCandidates.map((loc) => {
                          const isChecked = selectedIds.has(loc.location_id);
                          const isApproved = Boolean(loc.publish_google_business);
                          return (
                            <tr
                              key={loc.location_id}
                              className={`${isChecked ? "table-active-subtle" : ""} ${!isApproved ? "opacity-50" : ""}`}
                              onClick={() => toggleLocationSelection(loc.location_id)}
                              style={{ cursor: isApproved ? "pointer" : "not-allowed" }}
                            >
                              <td className="text-center" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={isChecked}
                                  disabled={!isApproved}
                                  onChange={() => toggleLocationSelection(loc.location_id)}
                                />
                              </td>
                              <td>
                                <div className="fw-bold text-primary-light">{loc.location_name}</div>
                                <span className="text-secondary-light font-monospace text-2xs">
                                  {loc.store_code}
                                </span>
                              </td>
                              <td>
                                <div>{loc.street_address || "—"}</div>
                                <span className="text-secondary-light text-2xs">
                                  {loc.city}, {loc.country || "DE"}
                                </span>
                              </td>
                              <td>
                                {isApproved ? (
                                  <span className="badge bg-success-50 text-success-700 border border-success-200 px-8 py-4 radius-4 text-2xs d-inline-flex align-items-center gap-1">
                                    <Icon icon="solar:check-circle-bold" /> Approved
                                  </span>
                                ) : (
                                  <span className="badge bg-neutral-100 text-neutral-600 border border-neutral-200 px-8 py-4 radius-4 text-2xs d-inline-flex align-items-center gap-1">
                                    <Icon icon="solar:lock-keyhole-minimalistic-linear" /> Disabled
                                  </span>
                                )}
                              </td>
                              <td>
                                {loc.has_coordinates ? (
                                  <span className="badge bg-success-50 text-success-700 border border-success-200 px-8 py-4 radius-4 text-2xs">
                                    {Number(loc.lat).toFixed(4)}, {Number(loc.lng).toFixed(4)}
                                  </span>
                                ) : (
                                  <span className="badge bg-warning-50 text-warning-700 border border-warning-200 px-8 py-4 radius-4 text-2xs">
                                    Missing
                                  </span>
                                )}
                              </td>
                              <td>
                                {loc.has_hours ? (
                                  <span className="badge bg-info-50 text-info-700 border border-info-200 px-8 py-4 radius-4 text-2xs">
                                    Configured
                                  </span>
                                ) : (
                                  <span className="badge bg-neutral-100 text-neutral-600 px-8 py-4 radius-4 text-2xs">
                                    None
                                  </span>
                                )}
                              </td>
                              <td>
                                <span className="badge bg-primary-50 text-primary-700 border border-primary-200 px-8 py-4 radius-4 text-2xs d-inline-flex align-items-center gap-1">
                                  <Icon icon="solar:gallery-wide-linear" /> {loc.photo_count}
                                </span>
                              </td>
                              <td>
                                {loc.gbp_status === "NEW" ? (
                                  <span className="badge bg-primary-600 text-white px-8 py-4 radius-4 text-2xs">
                                    NEW PROFILE
                                  </span>
                                ) : (
                                  <span className="badge bg-purple-50 text-purple-700 border border-purple-200 px-8 py-4 radius-4 text-2xs">
                                    UPDATE (v{loc.gbp_version || 1})
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Execution Notice */}
                <div className="alert bg-primary-50 border-primary-200 text-primary-900 radius-8 p-12 text-xs d-flex align-items-start gap-2 mb-0">
                  <Icon icon="solar:info-circle-bold" className="text-lg flex-shrink-0 text-primary-600 mt-1" />
                  <div>
                    <span className="fw-bold">Database-First Execution Order:</span> For each selected location, a new snapshot version will be committed to the MySQL database first. Next, Google Business Profile API will be called with the full payload (coordinates, address, opening hours, categories) followed by photos upload.
                  </div>
                </div>
              </>
            ) : (
              /* STEP 2 & 3: EXPORTING & PROGRESS BAR / COMPLETED */
              <div>
                {/* Progress Card */}
                <div className="card bg-neutral-50 border border-neutral-200 radius-12 p-20 mb-20">
                  <div className="d-flex align-items-center justify-content-between mb-8">
                    <div>
                      <h6 className="fw-bold text-primary-light mb-2">
                        {step === "exporting" ? "Exporting Locations to Google Business Profile..." : "Export Completed!"}
                      </h6>
                      <span className="text-xs text-secondary-light">
                        {step === "exporting"
                          ? `Processing location ${currentIndex} of ${exportStats.total || selectedIds.size}`
                          : `Successfully processed ${exportStats.total} location(s)`}
                      </span>
                    </div>
                    <div className="text-end">
                      <span className="display-6 fw-bold text-primary-600">{progressPercent}%</span>
                    </div>
                  </div>

                  {/* Animated Progress Bar */}
                  <div className="progress radius-8" style={{ height: "12px", backgroundColor: "#e2e8f0" }}>
                    <div
                      className={`progress-bar progress-bar-striped ${step === "exporting" ? "progress-bar-animated" : ""} bg-primary-600`}
                      role="progressbar"
                      style={{ width: `${progressPercent}%`, transition: "width 0.3s ease" }}
                      aria-valuenow={progressPercent}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    />
                  </div>

                  {/* Active Step Indicator when exporting */}
                  {step === "exporting" && activeItem && (
                    <div className="mt-16 p-12 bg-white radius-8 border border-neutral-200 d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center gap-2">
                        <Icon icon="line-md:loading-twotone-loop" className="text-primary-600 text-lg" />
                        <span className="text-xs fw-bold text-primary-light">
                          Current: {activeItem.location_name}
                        </span>
                      </div>
                      <div className="d-flex align-items-center gap-2 text-2xs">
                        <span className={`badge ${activeItemStep === "db" ? "bg-primary-600 text-white" : "bg-neutral-100 text-neutral-600"}`}>
                          1. Database
                        </span>
                        <span className={`badge ${activeItemStep === "api" ? "bg-primary-600 text-white" : "bg-neutral-100 text-neutral-600"}`}>
                          2. Google API
                        </span>
                        <span className={`badge ${activeItemStep === "photos" ? "bg-primary-600 text-white" : "bg-neutral-100 text-neutral-600"}`}>
                          3. Photos
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Metrics Summary Row */}
                <div className="row g-3 mb-20">
                  <div className="col-3">
                    <div className="card border radius-10 p-12 text-center bg-white shadow-none">
                      <span className="text-2xs text-secondary-light fw-semibold text-uppercase">Total</span>
                      <h5 className="mb-0 fw-bold text-primary-light mt-1">{exportStats.total || selectedIds.size}</h5>
                    </div>
                  </div>
                  <div className="col-3">
                    <div className="card border radius-10 p-12 text-center bg-success-50 border-success-200 shadow-none">
                      <span className="text-2xs text-success-700 fw-semibold text-uppercase">Created / Updated</span>
                      <h5 className="mb-0 fw-bold text-success-700 mt-1">{exportStats.created + exportStats.updated}</h5>
                    </div>
                  </div>
                  <div className="col-3">
                    <div className="card border radius-10 p-12 text-center bg-info-50 border-info-200 shadow-none">
                      <span className="text-2xs text-info-700 fw-semibold text-uppercase">Photos Synced</span>
                      <h5 className="mb-0 fw-bold text-info-700 mt-1">{exportStats.photosUploaded}</h5>
                    </div>
                  </div>
                  <div className="col-3">
                    <div className="card border radius-10 p-12 text-center bg-danger-50 border-danger-200 shadow-none">
                      <span className="text-2xs text-danger-700 fw-semibold text-uppercase">Failed</span>
                      <h5 className="mb-0 fw-bold text-danger-700 mt-1">{exportStats.failed}</h5>
                    </div>
                  </div>
                </div>

                {/* Live Activity Log Console */}
                <div className="card border border-neutral-300 radius-10 overflow-hidden shadow-none bg-neutral-900 text-neutral-100">
                  <div className="card-header bg-neutral-800 py-8 px-16 d-flex align-items-center justify-content-between border-0">
                    <span className="text-2xs fw-bold text-neutral-300 font-monospace">
                      <Icon icon="solar:code-square-linear" className="me-1" /> LIVE SYNC LOG
                    </span>
                    <span className="text-2xs text-neutral-400 font-monospace">{logs.length} entries</span>
                  </div>
                  <div
                    ref={logContainerRef}
                    className="card-body p-12 font-monospace text-2xs"
                    style={{ maxHeight: "200px", overflowY: "auto", backgroundColor: "#0f172a" }}
                  >
                    {logs.map((log) => (
                      <div key={log.id} className="mb-1 d-flex align-items-start gap-2">
                        <span className="text-neutral-500 flex-shrink-0">[{log.timestamp}]</span>
                        <span
                          className={
                            log.type === "error"
                              ? "text-danger-400"
                              : log.type === "success"
                              ? "text-success-400"
                              : log.type === "warning"
                              ? "text-warning-400"
                              : log.type === "process"
                              ? "text-info-300 fw-semibold"
                              : "text-neutral-300"
                          }
                        >
                          {log.message}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* MODAL FOOTER */}
          <div className="modal-footer border-top border-neutral-200 px-24 py-16 d-flex align-items-center justify-content-between bg-neutral-50">
            {step === "configure" ? (
              <>
                <button
                  type="button"
                  className="btn btn-outline-secondary px-20 py-8 radius-8 text-sm fw-semibold"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={!isConnected || selectedIds.size === 0 || !selectedAccountId}
                  className="btn btn-primary-600 px-24 py-8 radius-8 text-sm fw-semibold d-flex align-items-center gap-2"
                  onClick={handleStartExport}
                >
                  <Icon icon="solar:cloud-upload-bold" className="text-base" />
                  <span>Start Export to GBP ({selectedIds.size})</span>
                </button>
              </>
            ) : step === "exporting" ? (
              <>
                <span className="text-xs text-secondary-light">
                  <Icon icon="line-md:loading-twotone-loop" className="me-1" /> Synchronizing in progress... Please do not close this window.
                </span>
                <button
                  type="button"
                  className="btn btn-outline-danger px-20 py-8 radius-8 text-sm fw-semibold"
                  onClick={handleStopExport}
                >
                  Stop / Cancel
                </button>
              </>
            ) : (
              /* COMPLETED FOOTER */
              <>
                <button
                  type="button"
                  className="btn btn-outline-secondary px-20 py-8 radius-8 text-sm fw-semibold"
                  onClick={onClose}
                >
                  Close
                </button>
                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-primary px-20 py-8 radius-8 text-sm fw-semibold d-flex align-items-center gap-2"
                    onClick={() => {
                      onClose();
                      navigate("/google-business");
                    }}
                  >
                    <Icon icon="logos:google-icon" />
                    <span>View in Google Business</span>
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary-600 px-24 py-8 radius-8 text-sm fw-semibold"
                    onClick={onClose}
                  >
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportToGbpModal;

